import {
  Alert,
  Grid2,
  Paper,
  Typography,
  Backdrop,
  CircularProgress,
  Snackbar,
  IconButton,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import BarChartIcon from "@mui/icons-material/BarChart";
import GaugeComponent from "react-gauge-component";
import { useState } from "react";

import "../BusinessLeaderDashboard.css";
import {
  getValueForDashBoard,
  roundedNumberHighLevel,
} from "../helpers/businessleaderDashboardHelpers";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import React from "react";
import { useSearchParams } from "react-router-dom";

interface HighLevelKPIProps {
  highLevelData: any;
}

export const HighLevelKPI: React.FC<HighLevelKPIProps> = React.memo(
  ({ highLevelData }) => {
    // values fom business leader slice
    const { selectedSharedUidBui, selectedStartDate, selectedEndDate } =
      useSelector((state: any) => state?.businessLeaderDashBoard);

    const [isLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage] = useState("");
    const [isValid] = useState(false);
    const [searchParams] = useSearchParams();
    const selectedSharedUid = selectedSharedUidBui;
    const dimensions = searchParams.get("selectedDimension");
    const selectedType = searchParams.get("selectedType");
    const savedStartDate = selectedStartDate;
    const savedEndDate = selectedEndDate;

    const renderSnackbar = () => {
      return (
        <Snackbar
          sx={{ zIndex: "9999999" }}
          open={snackbarOpen}
          autoHideDuration={4000}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          onClose={() => setSnackbarOpen(false)}
        >
          <Alert
            elevation={6}
            variant="filled"
            onClose={() => setSnackbarOpen(false)}
            severity={
              snackbarMessage.includes("Error") || isValid ? "success" : "error"
            }
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      );
    };

    const getMetricValue = (metricName: string) => {
      const data = getValueForDashBoard(metricName, highLevelData) as any;
      if (data?.Proportional_Attainment) {
        return roundedNumberHighLevel(
          data?.Proportional_Attainment * 100
        ) as any;
      }
      return 0;
    };

    const startDate = dayjs(savedStartDate).format("YYYY-MM-DD");
    const enDDate = dayjs(savedEndDate).format("YYYY-MM-DD");
    const metricMapping = [
      { FTD: "FTD", tabMetricValue: 0 },
      { Profit: "PT", tabMetricValue: 0 },
      { NRPU: "PV", tabMetricValue: 1 },
    ] as any;

    const handleMetricClick = (metric: any) => {
      const firstKey = Object.keys(metric)[0];
      const graphMapping: { [key: string]: string } = {
        FTD: "ftd",
        Profit: "profit",
        NRPU: "nrpu",
      };
      const tabMapping: { [key: string]: number } = {
        FTD: 4,
        Profit: 6,
        NRPU: 5,
      };

      const graph = graphMapping[firstKey] || "ftd";
      const tab = tabMapping[firstKey] || 4;

      const params = new URLSearchParams();

      if (selectedType === "site") {
        const siteValues = selectedSharedUid.map((uid: any) =>
          uid?.value ? uid.value : uid
        );
        params.set("selectedSite", siteValues.join(","));
      } else if (
        selectedType === "organization" ||
        selectedType === "dimension"
      ) {
        if (dimensions) {
          params.set("dimension", dimensions);
        }
      }

      params.set("selectedStartDate", dayjs(startDate).format("DD-MM-YYYY"));
      params.set("selectedEndDate", dayjs(enDDate).format("DD-MM-YYYY"));

      params.set("dataSet", "click");
      params.set("view-Trends", "true");
      params.set("graph", graph);
      params.set("tab", tab.toString());

      const url = `/assets/overview/trends?${params.toString()}`;
      window.open(url, "_blank");
    };

    const getMetricIdTrack = (metric: any) => {
      switch (Object.keys(metric)[0]) {
        case "FTD":
          return "57";
        case "Profit":
          return "58";
        case "NRPU":
          return "59";
        default:
          return "57";
      }
    };

    return (
      <>
        {renderSnackbar()}
        <Box sx={{ padding: "8px 16px 0px", mb: 2, boxShadow: "none" }}>
          <Typography mb={2} fontSize={24}>
            High Level KPI's
          </Typography>
          <Grid2 container mt={0} columnSpacing={6} mb={1}>
            {Object.entries(metricMapping)?.map(([key, metric]) => (
              <Grid2 size={4} key={key}>
                <Paper sx={{ height: "210px", padding: "8px 16px" }}>
                  <Stack
                    data-tracker={getMetricIdTrack(metric)}
                    flexDirection={"row"}
                    justifyContent={"space-between"}
                    fontWeight={500}
                  >
                    <Typography
                      color="secondary"
                      sx={{
                        mr: 1,
                        padding: "0 !Important",
                        cursor: "pointer",
                        fontWeight: 500,
                      }}
                      onClick={() => handleMetricClick(metric)}
                    >
                      {Object.keys(metric as any)[0] as any}
                    </Typography>
                    <IconButton
                      disableRipple
                      size="small"
                      edge="end"
                      color="secondary"
                      sx={{
                        mr: 1,
                        padding: "0 !Important",
                        cursor: "pointer",
                      }}
                      onClick={() => handleMetricClick(metric)}
                    >
                      <BarChartIcon />
                    </IconButton>
                  </Stack>
                  <Stack
                    sx={{
                      width: "320px",
                      margin: "0px auto",
                      position: "relative",
                    }}
                  >
                    <GaugeComponent
                      type="semicircle"
                      arc={{
                        colorArray: ["#D81B60", "#FFC107", "#43A047"],
                        width: 0.4,
                        padding: 0.01,
                        cornerRadius: 0,
                      }}
                      pointer={{
                        type: "arrow",
                        animationDelay: 0,
                        length: 0.1,
                        strokeWidth: 0,
                        color: "#000000DE",
                      }}
                      labels={{
                        valueLabel: {
                          style: {
                            fontSize: 30,
                            fill: "rgba(0, 0, 0, 0.87)",
                            textShadow: "none",
                          },
                        },
                        tickLabels: {
                          type: "inner",
                          hideMinMax: true,
                        },
                      }}
                      value={getMetricValue(
                        Object.keys(metric as any)[0] as any
                      )}
                    />

                    <Stack className="legend">
                      <Typography sx={{ color: "#00000099", fontSize: 12 }}>
                        Low
                      </Typography>
                      <Typography sx={{ color: "#00000099", fontSize: 12 }}>
                        High
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>
              </Grid2>
            ))}
          </Grid2>
        </Box>

        {/* Add Backdrop with loader */}
        <Backdrop
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
          }}
          open={isLoading}
        >
          <CircularProgress color="secondary" />
        </Backdrop>
      </>
    );
  }
);
